<script setup lang="ts">
    import { useAuthStore } from '@/stores/auth.store';
    import { PhCaretDown, PhSignOut, PhX, PhUser, PhUserGear } from '@phosphor-icons/vue';
    import { onMounted, reactive, ref } from 'vue';
    import { useRouter } from 'vue-router';

    const authStore = useAuthStore();
    const router = useRouter();

    const isLogout = ref(false);
    const mobileMenuOpen = ref(false);

    const dropdown = reactive({
        notification: false,
        profile: false
    });
    const handleDropdownClick = (callback: () => void) => {
        dropdown.notification = false;
        dropdown.profile = false;
        callback();
    };

    const handleLogout = async () => {
        authStore.session = null;
        router.replace('/login');
    };

    const getProfile = async () => {
        await authStore.getProfile();
    };

    onMounted(async () => {
        getProfile();
    });
</script>

<template>
    <header
        class="fixed top-0 left-0 right-0 z-1001 bg-white"
        style="
            box-shadow:
                0px 1px 3px 0px rgba(0, 0, 0, 0.1),
                0px 1px 2px 0px rgba(0, 0, 0, 0.06);
        "
    >
        <div class="hidden md:flex justify-end items-center px-6 pl-[50px] py-4 relative">
            <div class="flex items-center gap-7">
                <!-- <div class="h-10 w-px bg-gray-200"></div> -->

                <!-- SECTION PROFILE USER -->
                <div ref="refProfile" class="w-fit relative">
                    <button
                        class="flex items-center gap-3 relative"
                        @click="dropdown.profile = !dropdown.profile"
                    >
                        <div
                            class="w-10 h-10 rounded-full object-cover border border-gray-200 text-gray-200 flex items-center justify-center"
                        >
                            <PhUser weight="regular" size="24" />
                        </div>
                        <div class="absolute top-1 left-8 rounded-full p-0.5 bg-[#FFF4E3]">
                            <img
                                src="@/assets/images/isOwnerCrown.svg"
                                alt="owner mark"
                                class="w-2 h-2"
                            />
                        </div>
                        <div class="flex flex-col gap-1 text-left">
                            <figure class="text-default-800">Super Admin</figure>
                            <figure class="text-secondary text-[10px]">
                                {{ authStore?.profile?.email }}
                            </figure>
                        </div>
                        <PhCaretDown :size="24" />
                    </button>
                    <!-- dropdown -->
                    <Transition
                        enter-active-class="transition-all duration-100 ease-out"
                        enter-from-class="opacity-0 scale-95 -translate-y-2"
                        enter-to-class="opacity-100 scale-100 translate-y-0"
                        leave-active-class="transition-all duration-100 ease-in"
                        leave-from-class="opacity-100 scale-100 translate-y-0"
                        leave-to-class="opacity-0 scale-95 -translate-y-2"
                    >
                        <div
                            v-show="dropdown.profile"
                            class="absolute top-12 -right-1 bg-white rounded-lg w-[328px] py-4 cursor-default space-y-3"
                            style="box-shadow: 0px 3px 8px 0px rgba(0, 0, 0, 0.24)"
                        >
                            <div class="flex flex-col items-start justify-start px-4 gap-1">
                                <p
                                    class="text-default-800 capitalize flex flex-row items-center gap-1"
                                >
                                    Super Admin
                                    <img
                                        src="@/assets/images/isOwnerCrown.svg"
                                        alt="owner mark"
                                        class="w-4 h-4"
                                    />
                                </p>
                                <figure class="text-neutral-600 lowercase">
                                    {{ authStore?.profile?.email }}
                                </figure>
                            </div>

                            <div class="flex flex-col items-start justify-start px-2 gap-4">
                                <p
                                    class="p-2 w-full inline-flex items-center gap-3 text-default-800 hover:bg-brand-50 hover:text-brand-500 rounded-lg cursor-pointer"
                                    @click="handleDropdownClick(() => (isLogout = true))"
                                >
                                    <PhSignOut size="24" />Keluar
                                </p>
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>
        </div>

        <!-- Mobile Navigation -->
        <div class="md:hidden">
            <!-- Mobile Header -->
            <div class="flex justify-end items-center px-4 py-3">
                <!-- Mobile Right Section -->
                <div class="flex items-center gap-8">
                    <!-- Mobile Profile -->
                    <button
                        class="flex items-center gap-2 relative"
                        @click="mobileMenuOpen = !mobileMenuOpen"
                    >
                        <div
                            class="w-8 h-8 rounded-full object-cover border border-gray-200 text-gray-200 flex items-center justify-center"
                        >
                            <PhUser weight="regular" size="24" />
                        </div>
                        <div class="absolute top-1 left-6 rounded-full p-0.5 bg-[#FFF4E3]">
                            <img
                                src="@/assets/images/isOwnerCrown.svg"
                                alt="owner mark"
                                class="w-2 h-2"
                            />
                        </div>
                        <p class="text-default-800 capitalize">Super Admin</p>
                        <PhCaretDown :size="20" />
                    </button>
                </div>
            </div>

            <!-- Mobile Menu (Dropdown) -->
            <div
                v-if="mobileMenuOpen"
                class="border border-gray-200 bg-white absolute top-12 right-3 rounded-lg w-2/3"
                style="box-shadow: 0px 3px 8px 0px rgba(0, 0, 0, 0.24)"
            >
                <div class="p-4">
                    <div class="flex flex-col items-start justify-start gap-1 py-2">
                        <p class="text-default-800 capitalize flex flex-row items-center gap-1">
                            Super Admin
                            <img
                                src="@/assets/images/isOwnerCrown.svg"
                                alt="owner mark"
                                class="w-4 h-4"
                            />
                        </p>
                        <figure class="text-neutral-600 lowercase">
                            {{ authStore?.profile?.email }}
                        </figure>
                    </div>

                    <p
                        class="pb-2 pt-3 items-center w-full inline-flex gap-3 rounded-lg cursor-pointer text-default-800"
                        @click="$router.push('/dashboard/profile')"
                    >
                        <PhUserGear size="24" />Informasi Akun
                    </p>

                    <p
                        class="pt-2 w-full inline-flex items-center gap-3 rounded-lg cursor-pointer text-default-800"
                        @click="$emit('logout')"
                    >
                        <PhSignOut size="24" />Keluar
                    </p>
                </div>
            </div>
        </div>
    </header>

    <!-- toast notification -->
    <!-- <ToastNotification
        :show="state.show"
        :dataNotification="state.message"
        @update:show="state.show = $event"
    /> -->

    <!-- MODAL DETAIL TRANSACTION -->
    <!-- <ModalTransactionDetail
    :transaction="notificationStore.transaction"
    :type="type"
    :is-open="modal.transaction"
    @close="modal.transaction = false"
  /> -->

    <!-- MODAL LOGOUT -->
    <div class="fixed inset-0 bg-gray-900/50 z-9999" v-if="isLogout"></div>
    <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
    >
        <div class="fixed inset-0 z-9999 flex items-center justify-center p-4" v-if="isLogout">
            <div class="bg-white rounded-2xl shadow-lg w-full max-w-[480px] relative">
                <div class="flex justify-between py-4 px-6 items-center">
                    <p class="text-xl font-semibold text-default-800">Keluar dari Akun</p>
                    <PhX class="cursor-pointer" size="24" @click="isLogout = false" />
                </div>
                <hr class="mb-4 border-default-200" />
                <div class="px-6">
                    <p class="text-default-800 leading-6">
                        Tindakan ini akan mengakhiri sesi Anda. Untuk mengakses data dan pengaturan,
                        Anda harus masuk kembali.
                    </p>
                </div>

                <hr class="mt-4 border-default-200" />

                <div class="flex justify-end gap-4 px-6 mt-3 mb-4">
                    <ButtonGhost
                        type="primary"
                        class="max-w-fit"
                        size="medium"
                        @click="isLogout = false"
                        >Batal</ButtonGhost
                    >
                    <ButtonPrimary
                        class="max-w-fit px-3!"
                        type="danger"
                        size="medium"
                        @click="handleLogout"
                        >Keluar</ButtonPrimary
                    >
                </div>
            </div>
        </div>
    </Transition>
</template>

<style scoped>
    @keyframes bounce {
        0%,
        20%,
        50%,
        80%,
        100% {
            transform: translateY(0);
        }
        40% {
            transform: translateY(-6px);
        }
        60% {
            transform: translateY(-2px);
        }
    }

    .notification-bounce {
        animation: bounce 0.9s infinite;
    }
</style>
